"use client";

import { useRouter } from "next/navigation";
import { useMemo } from "react";
import { decodeSku } from "@/lib/utils/decodeSku";
import { AbbreviationSKU } from "@/components/data/AbbreviationSKU";

interface Product {
    sku: string;
}

interface Props {
    products: Product[];
    pathname: string;
    activeFilters?: string[];
    totalItems?: number;
}

// گروه‌بندی فیلترها
type FilterGroup = "device" | "layer" | "port" | "feature" | "count";

interface FilterItem {
    key: string;
    title: string;
    sub?: string;
    count: number;
    portCount?: string; // عدد پورت (مثلاً "8" از "8g")
    group: FilterGroup;
    portCountNum: number; // برای مرتب‌سازی عددی پورت‌ها
}

/** تشخیص گروه یک key بر اساس AbbreviationSKU */
// ۴. تابع resolveGroup رو آپدیت کن
function resolveGroup(key: string, portCount: string): FilterGroup {
    if (portCount) return "port";
    if (key === "+") return "feature";

    const abbr = AbbreviationSKU.find(
        (i) => i.title.toLowerCase() === key.toLowerCase()
    );
    if (!abbr) return "feature";

    if (abbr.group?.includes("managed") || abbr.group?.includes("layer"))
        return "layer";

    if (abbr.group?.includes("number"))   // ← جدید
        return "count";

    if (
        abbr.group?.includes("switch") ||
        abbr.group?.includes("rack") ||
        abbr.group?.includes("fiber") ||
        abbr.group?.includes("brand")
    )
        return "device";

    return "feature";
}

const GROUP_LABELS: Record<FilterGroup, string> = {
    device: "نوع دستگاه",
    layer: "لایه مدیریتی",
    port: "تعداد پورت",
    count: "نوع پورت",
    feature: "ویژگی‌ها",
};

const GROUP_ORDER: FilterGroup[] = ["device", "layer", "port", "count", "feature"];

function CheckboxIcon({ checked }: { checked: boolean }) {
    return (
        <span
            className={[
                "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 transition-colors",
                checked
                    ? "border-primary bg-primary"
                    : "border-border bg-background group-hover:border-primary/60",
            ].join(" ")}
            aria-hidden="true"
        >
            {checked && (
                <svg
                    viewBox="0 0 10 8"
                    className="h-2.5 w-2.5 text-primary-foreground"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M1 4l2.5 2.5L9 1" />
                </svg>
            )}
        </span>
    );
}

export default function SubMenuSkuFilter({
                                             products,
                                             pathname,
                                             activeFilters = []
                                         }: Props) {
    const router = useRouter();

    /** ساخت map فیلترها با گروه‌بندی */
    const grouped = useMemo(() => {
        const countMap = new Map<string, number>();
        const portCountFromProducts = new Map<string, string>();
        const dynamicItems = new Map<string, FilterItem>(); // آیتم‌های dynamic مثل 8g, 16g

        products.forEach((product) => {
            const decoded = decodeSku(product.sku);
            decoded.parts.forEach((part) => {
                const key = part.key.toLowerCase();
                countMap.set(key, (countMap.get(key) || 0) + 1);

                if (part.count && !portCountFromProducts.has(key)) {
                    portCountFromProducts.set(key, part.count);
                }

                // اگر این key در AbbreviationSKU نیست → dynamic است (مثل 8g)
                const existsInAbbr = AbbreviationSKU.some(
                    (a) => a.title.toLowerCase() === key
                );
                if (!existsInAbbr && !dynamicItems.has(key)) {
                    const portCount = part.count || "";
                    const group = resolveGroup(key, portCount);
                    dynamicItems.set(key, {
                        key,
                        title: part.value,
                        sub: part.sub,
                        count: 0,
                        portCount,
                        group,
                        portCountNum: portCount ? parseInt(portCount, 10) : 0,
                    });
                }
            });

            if (decoded.parts.some(p => p.key === "poe")) {
                countMap.set("poe", (countMap.get("poe") || 0) + 1);
            }
        });

        const map = new Map<string, FilterItem>();

        // اول آیتم‌های AbbreviationSKU
        AbbreviationSKU.forEach((abbr) => {
            const key = abbr.title.toLowerCase();
            if (map.has(key)) return;
            const portCount = portCountFromProducts.get(key) || "";
            const group = resolveGroup(key, portCount);
            map.set(key, {
                key,
                title: abbr.content,
                sub: abbr.sub,
                count: countMap.get(key) || 0,
                portCount,
                group,
                portCountNum: portCount ? parseInt(portCount, 10) : 0,
            });
        });

        // بعد آیتم‌های dynamic رو اضافه کن
        dynamicItems.forEach((item, key) => {
            if (!map.has(key)) {
                item.count = countMap.get(key) || 0;
                map.set(key, item);
            }
        });

        // گروه‌بندی
        const groups: Record<FilterGroup, FilterItem[]> = {
            device: [],
            layer: [],
            port: [],
            count: [],
            feature: [],
        };

        for (const item of map.values()) {
            groups[item.group].push(item);
        }

        groups.device.sort((a, b) => a.title.localeCompare(b.title, "fa"));
        groups.layer.sort((a, b) => a.title.localeCompare(b.title, "fa"));
        groups.port.sort((a, b) => a.portCountNum - b.portCountNum);
        groups.feature.sort((a, b) => a.title.localeCompare(b.title, "fa"));
        groups.count.sort((a, b) => a.title.localeCompare(b.title, "fa"));

        return groups;
    }, [products]);

    /** multi-select toggle */
    const toggle = (key: string) => {
        const next = activeFilters.includes(key)
            ? activeFilters.filter((f) => f !== key)
            : [...activeFilters, key];

        if (next.length > 0) {
            // encode هر key جداگانه تا + تبدیل به %2B بشه
            const encoded = next.map(encodeURIComponent).join(",");
            router.push(`${pathname}?filters=${encoded}`);
        } else {
            router.push(pathname);
        }
    };

    const clearAll = () => router.push(pathname);
    const isAllActive = activeFilters.length === 0;

    const renderItem = (item: FilterItem) => {
        const checked = activeFilters.includes(item.key);
        return (
            <label
                key={item.key}
                onClick={() => toggle(item.key)}
                className={[
                    "group flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2 transition-colors",
                    checked ? "bg-primary/10 text-primary" : "hover:bg-muted",
                ].join(" ")}
            >
                <CheckboxIcon checked={checked} />

                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1 text-sm font-medium leading-snug hover:text-blue-600">
                        {item.portCount && (
                            <span className="font-bold">{item.portCount}</span>
                        )}
                        <span>{item.title}</span>
                    </div>

                    {item.sub && (
                        <div className="mt-0.5 text-xs text-muted-foreground">
                            {item.sub}
                        </div>
                    )}

                    <div className="mt-0.5 text-xs text-muted-foreground hidden">
                        {item.count.toLocaleString("fa-IR")} محصول
                    </div>
                </div>
            </label>
        );
    };

    return (
        <div className="space-y-4">

            {/* ── همه محصولات ──────────────────────────────── */}
            <label
                onClick={clearAll}
                className={[
                    "group flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors",
                    isAllActive ? "bg-primary/10 text-primary" : "hover:bg-muted",
                ].join(" ")}
            >
                <span
                    className={[
                        "flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 transition-colors",
                        isAllActive
                            ? "border-primary bg-primary"
                            : "border-border bg-background group-hover:border-primary/60",
                    ].join(" ")}
                    aria-hidden="true"
                >
                    {isAllActive && (
                        <svg
                            viewBox="0 0 10 8"
                            className="h-2.5 w-2.5 text-primary-foreground"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M1 4l2.5 2.5L9 1" />
                        </svg>
                    )}
                </span>
                <span className="text-sm font-medium">همه محصولات</span>
            </label>

            {/* ── گروه‌های فیلتر ───────────────────────────── */}
            {GROUP_ORDER.map((groupKey) => {
                const items = grouped[groupKey];
                if (items.length === 0) return null;
                return (
                    <div key={groupKey}>
                        {/* عنوان گروه */}
                        <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {GROUP_LABELS[groupKey]}
                        </p>
                        <div className="space-y-0.5">
                            {items.map(renderItem)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
